import { listPublishedEssaysOptions } from "@/actions/essay/handler";
import {
  type EssayCategory,
  essayCategories,
  essayCategoryLabels,
} from "@/api/db/schema/essay";
import { type EssayMetadata } from "@/components/admin/essayList";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { seo } from "@/utils/seo";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  BookOpen,
  Calculator,
  ChevronRight,
  Droplets,
  Dumbbell,
  Ruler,
  Search,
  TrendingUp,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";

const essaySearchSchema = z.object({
  category: z.string().optional(),
  tag: z.string().optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute("/_app/essay/")({
  validateSearch: (search) => essaySearchSchema.parse(search),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(listPublishedEssaysOptions());
  },
  head: () => ({
    meta: seo({
      title: "İçerikler ve Hesaplayıcılar | theCoach",
      description:
        "Powerlifting, güç sporları, beslenme ve antrenman üzerine eğitici blog yazıları ve performans hesaplayıcıları.",
      canonical: "https://atalaythecoach.com/essay",
    }),
  }),
  component: EssayListPageComponent,
});

interface CalculatorItem {
  title: string;
  categoryTag: string;
  href: string;
  icon: typeof Dumbbell;
}

const CALCULATORS: CalculatorItem[] = [
  {
    title: "1RM Hesaplayıcı",
    categoryTag: "Powerlifting & Güç",
    href: "/essay/1rm-calculator",
    icon: Dumbbell,
  },
  {
    title: "Wilks Skoru",
    categoryTag: "Powerlifting Katsayısı",
    href: "/essay/wilks-calculator",
    icon: TrendingUp,
  },
  {
    title: "MEV & MRV Hacim",
    categoryTag: "Set Hacmi Analizi",
    href: "/essay/mev-mrv-calculator",
    icon: Activity,
  },
  {
    title: "FFMI Hesaplayıcı",
    categoryTag: "Yağsız Kas Kütlesi",
    href: "/essay/ffmi-calculator",
    icon: Activity,
  },
  {
    title: "Kalori & TDEE",
    categoryTag: "Günlük Makro & Kalori",
    href: "/essay/calori-calculator",
    icon: Calculator,
  },
  {
    title: "Su İhtiyacı",
    categoryTag: "Hidrasyon Rehberi",
    href: "/essay/water-calculator",
    icon: Droplets,
  },
  {
    title: "Body Fat (Yağ Oranı)",
    categoryTag: "Vücut Kompozisyonu",
    href: "/essay/body-fat-calculator",
    icon: Ruler,
  },
];

function EssayListPageComponent() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const selectedCategory = (searchParams.category || "all") as
    | EssayCategory
    | "all";
  const selectedTag = searchParams.tag || "";
  const [searchQuery, setSearchQuery] = useState(searchParams.q || "");
  const [mobileTab, setMobileTab] = useState<"essays" | "calculators">(
    "essays",
  );

  const essaysQuery = useSuspenseQuery(listPublishedEssaysOptions());
  const essays = (essaysQuery.data.body?.essays || []) as EssayMetadata[];

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  const filteredEssays = useMemo(() => {
    return essays.filter((essay) => {
      if (selectedCategory !== "all" && essay.category !== selectedCategory) {
        return false;
      }
      if (selectedTag) {
        const essayTags = Array.isArray(essay.tags) ? essay.tags : [];
        if (!essayTags.includes(selectedTag)) {
          return false;
        }
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const titleMatch = essay.title?.toLowerCase().includes(q);
        const excerptMatch = (essay.metaDescription || essay.excerpt)
          ?.toLowerCase()
          .includes(q);
        const tagMatch = Array.isArray(essay.tags)
          ? essay.tags.some((t) => t.toLowerCase().includes(q))
          : false;

        if (!titleMatch && !excerptMatch && !tagMatch) {
          return false;
        }
      }
      return true;
    });
  }, [essays, selectedCategory, selectedTag, searchQuery]);

  const setCategory = (cat: string) => {
    navigate({
      to: "/essay",
      search: (prev) => {
        const next = { ...prev, category: cat === "all" ? undefined : cat };
        if (!next.category) delete next.category;
        return next;
      },
    });
  };

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-background pt-20 text-foreground sm:pt-24">
      <div className="shrink-0 border-b bg-background/95 pb-3 backdrop-blur-sm">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-center">
            <div>
              <h1 className="font-extrabold text-2xl tracking-tight sm:text-3xl">
                İçerikler ve Hesaplayıcılar
              </h1>
              <p className="text-muted-foreground text-xs sm:text-sm">
                Spor, beslenme ve performans üzerine yazılar, rehberler ve
                araçlar.
              </p>
            </div>
          </div>

          <div className="mt-2.5 flex items-center rounded-lg bg-muted/70 p-1 lg:hidden">
            <button
              type="button"
              onClick={() => setMobileTab("essays")}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 font-medium text-xs transition-all",
                mobileTab === "essays"
                  ? "bg-background font-semibold text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <BookOpen className="h-3.5 w-3.5" />
              Yazılar ({filteredEssays.length})
            </button>
            <button
              type="button"
              onClick={() => setMobileTab("calculators")}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 font-medium text-xs transition-all",
                mobileTab === "calculators"
                  ? "bg-background font-semibold text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Calculator className="h-3.5 w-3.5 text-primary" />
              Hesaplayıcılar ({CALCULATORS.length})
            </button>
          </div>

          <div
            className={cn(
              "scrollbar-none mt-3 items-center gap-1.5 overflow-x-auto pb-0.5",
              mobileTab === "essays" ? "flex" : "hidden lg:flex",
            )}
          >
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory("all")}
              className="h-7 rounded-full px-3 text-xs"
            >
              Tümü ({essays.length})
            </Button>
            {essayCategories.map((cat) => {
              const count = essays.filter((e) => e.category === cat).length;
              return (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCategory(cat)}
                  className="h-7 rounded-full px-3 text-xs"
                >
                  {essayCategoryLabels[cat]?.label || cat}
                  {count > 0 && (
                    <span className="ml-1 opacity-70">({count})</span>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <main className="container mx-auto grid h-full max-w-6xl grid-cols-1 gap-6 px-4 py-3 lg:grid-cols-12">
          <div
            className={cn(
              "h-full min-h-0 flex-col lg:col-span-8 lg:flex",
              mobileTab === "essays" ? "flex" : "hidden lg:flex",
            )}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b pb-2">
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-lg sm:text-xl">
                  {selectedCategory === "all"
                    ? "Son Yazılar"
                    : `${essayCategoryLabels[selectedCategory]?.label || ""} Yazıları`}
                </h2>
                <span className="text-muted-foreground text-xs">
                  ({filteredEssays.length})
                </span>
              </div>

              <div className="relative w-44 sm:w-56">
                <Search className="-translate-y-1/2 absolute top-1/2 left-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Yazılarda ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-7.5 rounded-lg pr-7 pl-8 text-xs"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="-translate-y-1/2 absolute top-1/2 right-2 text-muted-foreground hover:text-foreground"
                    aria-label="Aramayı temizle"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {filteredEssays.length === 0 ? (
              <div className="my-auto rounded-xl border border-border/70 border-dashed p-8 text-center text-muted-foreground">
                <p className="text-sm">
                  {searchQuery
                    ? `"${searchQuery}" aramasıyla eşleşen yazı bulunamadı.`
                    : "Bu kategoride henüz yayınlanmış yazı bulunamadı."}
                </p>
                {(searchQuery || selectedCategory !== "all") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      if (selectedCategory !== "all") setCategory("all");
                    }}
                    className="mt-3"
                  >
                    Filtreleri Temizle
                  </Button>
                )}
              </div>
            ) : (
              <div className="scrollbar-thin min-h-0 flex-1 space-y-5 divide-y divide-border/60 overflow-y-auto pt-1 pr-2">
                {filteredEssays.map((essay, idx) => {
                  const categoryInfo =
                    essayCategoryLabels[essay.category as EssayCategory] ||
                    essayCategoryLabels.general;
                  const essayTags = Array.isArray(essay.tags) ? essay.tags : [];

                  return (
                    <Link
                      key={essay.essayId}
                      to="/essay/$essayId"
                      params={{ essayId: essay.essayId }}
                      className={`group block ${idx > 0 ? "pt-5" : "pt-1"}`}
                    >
                      <article className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-5">
                        <div className="sm:col-span-2">
                          {(categoryInfo.label !== "Genel" ||
                            essayTags.length > 0) && (
                            <div className="mb-1.5 flex flex-wrap items-center gap-1">
                              {categoryInfo.label !== "Genel" && (
                                <Badge
                                  variant="secondary"
                                  className="h-5 px-2 text-[10px]"
                                >
                                  {categoryInfo.label}
                                </Badge>
                              )}
                              {essayTags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-[11px] text-muted-foreground"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}

                          <h3 className="font-bold text-base tracking-tight transition-colors group-hover:text-primary sm:text-lg">
                            {essay.title}
                          </h3>
                          {(essay.metaDescription || essay.excerpt) && (
                            <p className="mt-1 line-clamp-2 text-muted-foreground text-xs leading-relaxed sm:text-sm">
                              {essay.metaDescription || essay.excerpt}
                            </p>
                          )}
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            {formatDate(essay.publishedAt)} · {essay.readTime}
                          </p>
                        </div>

                        {essay.coverImageUrl && (
                          <div className="h-32 w-full overflow-hidden rounded-lg sm:h-full">
                            <img
                              src={essay.coverImageUrl}
                              alt={essay.title}
                              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                          </div>
                        )}
                      </article>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <aside
            className={cn(
              "h-full min-h-0 flex-col lg:col-span-4 lg:flex",
              mobileTab === "calculators" ? "flex" : "hidden lg:flex",
            )}
          >
            <div className="flex h-full flex-col rounded-xl border border-border/60 bg-card/60 p-4 backdrop-blur-sm">
              <div className="mb-3 flex shrink-0 items-center justify-between border-b pb-2.5">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Calculator className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm leading-none">
                      Hesaplayıcılar
                    </h3>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      Performans ve analiz araçları
                    </p>
                  </div>
                </div>
                <span className="font-medium text-[11px] text-muted-foreground">
                  {CALCULATORS.length} Araç
                </span>
              </div>

              <div className="scrollbar-thin min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
                {CALCULATORS.map((calc) => {
                  const CalcIcon = calc.icon;
                  return (
                    <Link
                      key={calc.href}
                      to={calc.href}
                      className="group flex items-center justify-between rounded-lg border border-border/40 bg-background/50 p-2.5 transition-all hover:border-primary/50 hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
                          <CalcIcon className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="font-medium text-xs transition-colors group-hover:text-primary sm:text-sm">
                            {calc.title}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {calc.categoryTag}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </aside>
        </main>
      </div>
    </div>
  );
}
